import $ from "jquery";
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { DsnManuelleDto } from "../../../dto/GenApi.iNotPaie.Dto/DsnManuelleDto";
import { DsnNatureDeclaration } from "../../../dto/GenApi.iNotPaie.Dto/DsnNatureDeclaration";
import { NotificationServiceInstance } from "../../../shared/services/notification.service";
import { EventBus } from "../../Controls/EventBus";
import EventConstants from "../../Controls/EventConstants";
import { ServicesFactoryInstance } from "../../Controls/ServicesFactory";
import { ModalAlerteOptions } from "../../modalComponents/modalAlerte/modalAlerte";

/**
 * Composant Vue pour l'édition et l'envoi manuel d'une DSN.
 */
@Component({
    components: {
        Loader: require("./../../../shared/components/Loader/Loader.vue.html")
    }
})
export default class ModalEditionDsnComponent extends Vue {
    public dataReady: boolean = false;
    public dsn: DsnManuelleDto = { idDsnMetadata: 0, File: "" };
    public contenuDsn?: string = "";
    public dsnNature?: DsnNatureDeclaration = undefined;
    public idDsnMetadata: number = 0;

    public rafraichissementEnCours: boolean = false;

    /**
     * Initialise la modale d'édition de la DSN.
     * @param idDsnMetadata Identifiant de la DSN à charger
     * @param integreOeth Indique si l'intégration OETH est activée
     * @param nature Nature de la déclaration DSN
     */
    public Init(idDsnMetadata: number, integreOeth: boolean, nature: DsnNatureDeclaration): void {
        this.resetData();

        this.dataReady = false;
        this.dsnNature = nature;
        this.idDsnMetadata = idDsnMetadata;

        ServicesFactoryInstance.DsnService.getDsnDto(idDsnMetadata, integreOeth, data => {
            this.dsn = data;
            this.contenuDsn = this.dsn.File;
            this.dataReady = true;
        }, () => {
            this.dataReady = true;
            NotificationServiceInstance.SendFailed("Une erreur est survenue lors de la récupération des informations");
        });
    }

    /**
     * Récupère le fichier de la dernière DSN déjà générée sans régénération.
     */
    public rafraichir(): void {
        if (this.idDsnMetadata === 0) {
            NotificationServiceInstance.SendFailed("Contexte de rafraîchissement manquant");
            return;
        }

        this.dataReady = false;
        this.rafraichissementEnCours = true;
        ServicesFactoryInstance.DsnService.getDernierFichierDsn(this.idDsnMetadata, data => {
            this.dsn = data;
            this.contenuDsn = this.dsn.File;
            this.rafraichissementEnCours = false;
            this.dataReady = true;
        }, () => {
            this.rafraichissementEnCours = false;
            this.dataReady = true;
            NotificationServiceInstance.SendFailed("Aucune DSN générée trouvée pour ce contexte");
        });
    }

    /**
     * Déclenche l'envoi de la DSN si elle est valide.
     */
    public envoyer(): void {
        if (this.dsnValide()) {
            // Faire dépôt
            this.faireDepotManuel();
        }
    }

    /**
     * Exporte le fichier DSN en local après nettoyage du contenu et du nom.
     */
    public exportDsnFile(): void {
        if (this.dsn.File === undefined || this.dsn.FileName === undefined)
            return;

        // Supprime les accents et les caractères spéciaux du contenu du fichier DSN
        const cleanedFile = this.cleanseString(this.dsn.File);
        const fileName = this.trimDsnFileName(this.dsn.FileName);

        const blob = new Blob([cleanedFile], { type: 'plain/text' });
        this.telechargerPdf(blob, fileName + ".dsn");
    }

    /**
     * Effectue le dépôt manuel de la DSN.
     */
    public faireDepotManuel(): void {
        this.dataReady = false;
        ServicesFactoryInstance.DsnService.envoyerDepotManuel(this.dsn, () => {
            $("#modalEditionDsnComponent").modal("hide");
            this.$emit("Fermeture", this.dsnNature);
        }, (reason: any) => {
            this.dataReady = true;
            NotificationServiceInstance.SendFailed("Une erreur est survenue lors du dépôt de la dsn");

            // On ouvre la modale uniquement pour les erreurs métier.
            if (typeof reason === "string" && reason.includes("S21.G00.23.001")) {
                // Enlève le code d'erreur du message
                const message = reason.replace("Erreur 417 - ", "");
                const modalOptions = new ModalAlerteOptions({
                    title: "DSN bloquée",
                    message,
                    canClose: true,
                    modalClass: "dsn-modal"
                });
                EventBus.$emit(EventConstants.MODAL_ALERT, modalOptions);
            }
        });
    }

    /**
     * Valide le contenu de la DSN avant envoi.
     * @returns true si la DSN est valide, false sinon
     */
    public dsnValide(): boolean {
        const rubriqueNombreLigne = "S90.G00.90.001";

        // Contrôle validite fichier DSN
        if (this.dsn == undefined || this.contenuDsn == undefined || this.contenuDsn == "") {
            NotificationServiceInstance.SendFailed("Le fichier dsn est incorrect");

            return false;
        }

        // Contrôle rubrique S90.G00.90.001 présente
        if (this.contenuDsn.toUpperCase().indexOf(rubriqueNombreLigne) >= 0) {
            // Contrôle si la rubrique apparaît plusieurs fois
            if ((this.contenuDsn.toUpperCase().match(/S90.G00.90.001/g) || []).length > 1) {
                NotificationServiceInstance.SendFailed("La rubrique S90.G00.90.001 apparaît plusieurs fois");

                return false;
            } else {
                // Contrôle nombre de ligne
                const listeRubriques = this.contenuDsn.split("\n");
                let file = "";
                const nombreDeLigne = listeRubriques.length;
                for (let i = 0; i < listeRubriques.length; i++) {
                    if (listeRubriques[i].toUpperCase().indexOf(rubriqueNombreLigne) >= 0) {
                        listeRubriques[i] = `${rubriqueNombreLigne},'${nombreDeLigne}'`;
                    }

                    file += listeRubriques[i] + "\n";
                }

                this.dsn.File = file;
            }
        } else {
            NotificationServiceInstance.SendFailed("Rubrique S90.G00.90.001 manquante");

            return false;
        }

        return true;
    }

    /**
     * Annule l'édition et ferme la modale.
     */
    public annuler(): void {
        this.resetData();
        $("#modalEditionDsnComponent").modal("hide");
    }

    /**
     * Réinitialise les données de la modale.
     */
    private resetData(): void {
        this.dsn = { idDsnMetadata: 0, File: "", FileName: "" };
        this.contenuDsn = "";
        this.idDsnMetadata = 0;
        this.dsnNature = undefined;
    }

    /**
     * Retire la partie code société et numéro d'ordre du nom du fichier dsn.
     * @param input Nom du fichier DSN d'origine
     * @returns Nom du fichier DSN nettoyé
     */
    private trimDsnFileName(input: string): string {
        const parts = input.split("_");

        if (parts.length < 3) return input;
        const trimmedParts = parts.slice(0, -2);

        return trimmedParts.join("_");
    }

    /**
     * Retire les accents et les caractères spéciaux du fichier.
     * @param text Texte à nettoyer
     * @returns Texte nettoyé
     */
    private cleanseString(text: string): string {
        return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }

    /**
     * Télécharge un fichier.
     * @param blob Blob à télécharger
     * @param name Nom du fichier
     */
    private telechargerPdf(blob: Blob, name: string): void {
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, name);
        } else {
            const link = document.createElement("a");
            document.body.appendChild(link);

            link.href = window.URL.createObjectURL(blob);
            link.download = name;
            link.click();
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/dsn/modalEditionDsn/modalEditionDsn.ts